import { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box, getCSSVariable, Link, PressableBox, Text } from '@opendoor/bricks-next';
import { IconHome, IconLogout2 } from '@tabler/icons-react';
import { useHasActiveBuyerOffers } from 'helpers/useBuyerOffers';

import { useObservability } from '../../helpers/observability';
import { Lead, useLeads } from '../../helpers/useLeads';
import { getSessionExtras } from '../api';

interface AccountMenuProps {
  isExpanded: boolean;
  isLoggedIn: boolean;
}

const AccountMenu = (props: AccountMenuProps) => {
  const { trackEvent } = useObservability();
  const helpCenterUrl = 'https://help.opendoor.com';
  const { user: human, logout } = useAuth();
  const [hasResaleDashboard, setHasResaleDashboard] = useState(false);
  const leads = useLeads();
  const { hasActiveOffers } = useHasActiveBuyerOffers(props.isExpanded);
  const hasLeads = leads && leads.length > 0;

  useEffect(() => {
    if (!human) {
      return;
    }
    getSessionExtras().then((extras) => {
      setHasResaleDashboard(extras.has_resale_closing_dashboard);
    });
  }, [human?.humanToken, human?.customerUuid]);

  const handleSignOutClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  if (!props.isLoggedIn) {
    return (
      <PrimaryNavSubmenu isExpanded={props.isExpanded}>
        <PrimaryNavItem>
          <MenuItem href={helpCenterUrl}>Help center</MenuItem>
        </PrimaryNavItem>
      </PrimaryNavSubmenu>
    );
  }

  return (
    <PrimaryNavSubmenu isExpanded={props.isExpanded}>
      {hasLeads &&
        leads.slice(0, 3).map((lead: Lead) => (
          <PrimaryNavItem key={lead.id}>
            <MenuItem href={lead.url}>
              {lead.address.street}
              {lead.readable_offer_state && ` (${lead.readable_offer_state})`}
            </MenuItem>
          </PrimaryNavItem>
        ))}
      {leads && leads.length > 3 && (
        <PrimaryNavItem>
          <MenuItem href="/properties">More</MenuItem>
        </PrimaryNavItem>
      )}
      {hasLeads && <Divider />}
      {hasResaleDashboard && (
        <PrimaryNavItem>
          <MenuItem href="/closing-dashboard">Closing dash</MenuItem>
        </PrimaryNavItem>
      )}
      {hasResaleDashboard && <Divider />}
      {hasActiveOffers && (
        <PrimaryNavItem>
          <MenuItem
            icon={
              <IconHome size={20} stroke={1.5} color={getCSSVariable('$contentPrimary', 'color')} />
            }
            onClick={() =>
              trackEvent('cta-click', 'dashboard-link', undefined, {
                categoryOverride: 'homepage',
              })
            }
            href="/dashboard"
          >
            Dashboard
          </MenuItem>
        </PrimaryNavItem>
      )}
      <PrimaryNavItem>
        <MenuItem
          // TODO: remove categoryOverride
          // observability is initialized with 'cosmos' as the category for all events
          onClick={() =>
            trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
          }
          href="/account"
        >
          Account
        </MenuItem>
      </PrimaryNavItem>
      <Divider />
      <PrimaryNavItem>
        <MenuItem href={helpCenterUrl}>Help center</MenuItem>
      </PrimaryNavItem>
      <PrimaryNavItem>
        <MenuButton
          onPress={handleSignOutClick}
          label="Sign out"
          icon={
            <IconLogout2
              size={20}
              stroke={1.5}
              color={getCSSVariable('$contentPrimary', 'color')}
            />
          }
          analyticsName="cosmos-account-menu-sign-out"
        />
      </PrimaryNavItem>
    </PrimaryNavSubmenu>
  );
};

const PrimaryNavSubmenu: React.FC<{ isExpanded: boolean; children: React.ReactNode }> = ({
  isExpanded,
  children,
}) => (
  <Box
    {...(!isExpanded ? { inert: '' } : {})}
    position="absolute"
    // 100% clears the avatar; +12px covers the remaining space between the
    // avatar bottom and the header bar bottom (avatar is vertically centered)
    top="calc(100% + 12px)"
    right={0}
    backgroundColor="$backgroundPrimary"
    borderRadius="$3x"
    borderWidth={1}
    borderColor="$borderPrimary"
    minWidth={198}
    padding="$2x"
    gap="$2x"
    overflow="hidden"
    zIndex={1000}
    opacity={isExpanded ? 1 : 0}
    aria-hidden={!isExpanded}
    tag="ul"
    style={{
      visibility: isExpanded ? 'visible' : 'hidden',
      transition: 'opacity 0.2s ease, visibility 0.2s ease',
      listStyle: 'none',
      margin: 0,
    }}
  >
    {children}
  </Box>
);

const PrimaryNavItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box tag="li">{children}</Box>
);

const Divider = () => <Box role="separator" height={1} backgroundColor="$borderSecondary" />;

interface MenuItemProps {
  href: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ href, children, icon, onClick }) => (
  <Link href={href} onPress={onClick} analyticsName="" color="$contentPrimary">
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      gap="$4x"
      paddingVertical="$4x"
      paddingRight="$6x"
      paddingLeft={icon ? '$4x' : 30}
      borderRadius="$2x"
      hoverStyle={{ backgroundColor: '$backgroundStateHoverPrimary' }}
      cursor="pointer"
    >
      {icon && (
        <Box width={20} height={20} display="flex" alignItems="center" justifyContent="center">
          {icon}
        </Box>
      )}
      <Text tag="span" typography="$bodyMedium" color="$contentPrimary" whiteSpace="nowrap">
        {children}
      </Text>
    </Box>
  </Link>
);

interface MenuButtonProps {
  onPress: () => void;
  label: string;
  icon?: React.ReactNode;
  analyticsName: string;
}

const MenuButton: React.FC<MenuButtonProps> = ({ onPress, label, icon, analyticsName }) => (
  <PressableBox
    onPress={onPress}
    display="flex"
    flexDirection="row"
    alignItems="center"
    justifyContent="flex-start"
    gap="$4x"
    paddingVertical="$4x"
    paddingRight="$6x"
    paddingLeft={icon ? '$4x' : 30}
    borderRadius="$2x"
    hoverStyle={{ backgroundColor: '$backgroundStateHoverPrimary' }}
    cursor="pointer"
    aria-label={label}
    analyticsName={analyticsName}
  >
    {icon && (
      <Box width={20} height={20} display="flex" alignItems="center" justifyContent="center">
        {icon}
      </Box>
    )}
    <Text tag="span" typography="$bodyMedium" color="$contentPrimary" whiteSpace="nowrap">
      {label}
    </Text>
  </PressableBox>
);

export default AccountMenu;
